import { type ComputedRef, computed } from 'vue'
import { useQuery, useMutation } from '/@/core/graphql'
import { gql } from '@apollo/client/core'
import type { ClientInput, ClientWithInvoices } from './types'
import { clientPropsFragment } from './fragments'
import { useClientCache } from './utils'

export const getClientsGqlQuery = gql`
  ${clientPropsFragment}
  query getAllDesignerClients($id: String!) {
    designer(id: $id) {
      id
      clients {
        ...ClientProps
      }
    }
  }
`

export function useClients(designerId: ComputedRef<string>) {
  const { result, loading } = useQuery<{
    designer: { clients: ClientWithInvoices[] }
  }>(getClientsGqlQuery, {
    id: designerId,
  })

  const clients = computed(() => result.value?.designer.clients ?? [])

  return { clients, loading }
}

export function useClientsApi() {
  const clientCache = useClientCache()

  const { mutate: create } = useMutation(gql`
    ${clientPropsFragment}
    mutation createNewClient($input: ClientInput!) {
      client {
        create(input: $input) {
          record {
            ...ClientProps
          }
        }
      }
    }
  `)

  const { mutate: update } = useMutation(gql`
    ${clientPropsFragment}
    mutation updateClientInfo($id: String!, $input: ClientInput!) {
      client {
        update(id: $id, input: $input) {
          record {
            ...ClientProps
          }
        }
      }
    }
  `)

  const { mutate: remove } = useMutation(gql`
    mutation deleteClient($id: String!) {
      client {
        delete(id: $id) {
          recordId
        }
      }
    }
  `)

  return {
    create(input: ClientInput) {
      return create(
        { input },
        {
          update(cache, { data }) {
            clientCache.addClient(cache, data.client.create.record)
          },
        },
      )
    },
    update(id: string, input: ClientInput) {
      update({
        id,
        input,
      })
    },
    remove(id: string) {
      remove(
        { id },
        {
          update(cache, { data }) {
            clientCache.deleteClient(cache, data.client.delete.recordId)
          },
        },
      )
    },
  }
}
